import { ShadowBarTitle } from '@/components/ShadowBarChart/ShadowBarTitle';
import { twCn } from '@/utils';
import React, { forwardRef, ReactNode } from 'react';
import { useBarChartContext } from './context';

type ShadowBarProps = { height: number; children?: ReactNode } & (
    | {
          variant: 'primary' | 'secondary';
          backgroundColor?: never;
      }
    | { backgroundColor: string; variant?: never }
);

const ShadowBarBase = forwardRef(
    (
        { height, variant, children, backgroundColor }: ShadowBarProps,
        ref: any,
    ) => {
        const { maxValue, height: chartHeight, minValue } = useBarChartContext();

        const multiplier = chartHeight / maxValue;
        const negativeOffset = Math.abs(Math.min(minValue ?? 0, 0)) * multiplier;
        const bottomPx = negativeOffset + Math.min(height, 0) * multiplier;
        const barHeightPx = Math.abs(height) * multiplier;
        const isNegative = height < 0;

        return (
            <div
                ref={(r) => {
                    if (ref?.current) {
                        return;
                    }
                    if (ref) ref.current = r;
                }}
                style={{
                    bottom: bottomPx,
                    height: isNegative ? barHeightPx : undefined,
                }}
                className={twCn(
                    'absolute transition-all duration-300',
                    {
                        'left-2 right-2 group-hover:left-1 group-hover:right-1':
                            variant === 'primary' || backgroundColor,
                        'left-0 right-0 group-hover:-left-1 group-hover:-right-1':
                            variant === 'secondary',
                    },
                )}
            >
                {isNegative && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                        }}
                    >
                        {children}
                    </div>
                )}
                {!isNegative && children}
                <div
                    style={{
                        height: barHeightPx,
                        backgroundColor: backgroundColor,
                    }}
                    className={twCn({
                        'rounded-t-md': !isNegative,
                        'rounded-b-md': isNegative,
                        'bg-[#2B67FF]': variant === 'primary',
                        'bg-indigo-300/20 group-hover:bg-indigo-300/40':
                            variant === 'secondary',
                    })}
                ></div>
            </div>
        );
    },
);

ShadowBarBase.displayName = 'ShadowBar';

export const ShadowBar = Object.assign(ShadowBarBase, {
    Title: ShadowBarTitle,
});
